import { useEffect, useState } from "preact/hooks";
import { subscribeRecords } from "../character/records";
import type { ConflictCopy, DeletedRecord, ParkedRecord, SyncSession, SyncStatus } from "../sync";
import { appEngine, syncIndicator } from "../sync";

/**
 * What every Sync screen reads (issue #26): the engine's status, the signed-in session, and the
 * three lists the home screen and the Character page show. Each hook subscribes to the status
 * and to the record feed, so a sync that lands, a Character another Device deleted and a save
 * made here all refresh the screen without it being reopened.
 */

export function useSyncStatus(): SyncStatus {
  const [status, setStatus] = useState<SyncStatus>(() => appEngine().status());
  useEffect(() => appEngine().subscribe(setStatus), []);
  return status;
}

/** Undefined until it has been read; null when this Device is not signed in. */
export function useSyncSession(): SyncSession | null | undefined {
  const status = useSyncStatus();
  const [session, setSession] = useState<SyncSession | null | undefined>();
  useEffect(() => {
    let live = true;
    appEngine()
      .session()
      .then((s) => live && setSession(s), () => live && setSession(null));
    return () => {
      live = false;
    };
  }, [status]);
  return session;
}

/**
 * One list off the engine, reloaded whenever the status moves or a record changes. The loader is
 * called by identity, so pass a module-level function rather than one built in the render.
 */
function useSyncList<T>(load: () => Promise<T[]>): T[] {
  const status = useSyncStatus();
  const [rows, setRows] = useState<T[]>([]);
  const [tick, setTick] = useState(0);
  useEffect(() => subscribeRecords(() => setTick((n) => n + 1)), []);
  useEffect(() => {
    let live = true;
    load().then((next) => live && setRows(next), () => undefined);
    return () => {
      live = false;
    };
  }, [load, status, tick]);
  return rows;
}

const loadConflicts = (): Promise<ConflictCopy[]> => appEngine().conflictCopies();
const loadParked = (): Promise<ParkedRecord[]> => appEngine().parked();
const loadDeleted = (): Promise<DeletedRecord[]> => appEngine().recentlyDeleted();

/** Every Character kept as the losing half of a conflict, newest first. */
export function useConflictCopies(): ConflictCopy[] {
  return useSyncList(loadConflicts);
}

/** Every record the Account holds that this Device cannot apply yet. */
export function useParked(): ParkedRecord[] {
  return useSyncList(loadParked);
}

/** Every deletion this Device can still undo. */
export function useRecentlyDeleted(): DeletedRecord[] {
  return useSyncList(loadDeleted);
}

/**
 * The Sync indicator (issue #26), in the style of the update indicator of #3: a tag on the home
 * header and in the Character menu when changes are waiting, a conflict is kept, or syncing has
 * stopped until the person acts. Nothing at all on a Device that is not signed in.
 */
export function SyncIndicator({ link = true }: { link?: boolean }) {
  const status = useSyncStatus();
  const conflicts = useConflictCopies();
  const conflicted = conflicts.length > 0;
  const text = conflicted ? `${conflicts.length} conflict ${conflicts.length === 1 ? "copy" : "copies"}` : syncIndicator(status);
  if (!text) return null;
  const attention = conflicted || status.phase === "blocked" || status.phase === "failed";
  const cls = `tag ${attention ? "warn" : "open"} sync-indicator`;
  if (!link) return <span class={cls}>{text}</span>;
  // Conflicts are settled on the Character list; everything else is explained by the Sync card.
  const target = conflicted ? "characters" : "sync";
  return (
    <a href="#/" class={cls} onClick={() => setTimeout(() => document.getElementById(target)?.scrollIntoView({ block: "start" }), 50)}>
      {text}
    </a>
  );
}
